import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Truck, Package, Clock, AlertCircle, CheckCircle, ArrowLeft, Loader2, MapPin, Hash, User } from 'lucide-react';
import orderService from '../services/order.service';
import { sanitizeError } from '../utils/errorUtils';

const PickupRequestDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [pickupRequest, setPickupRequest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            // Assuming there's an endpoint to get single pickup request details
            // If not, we might need to filter from the list or add one to backend
            const response = await fetch(`/api/pickup-requests/${id}`).then(res => res.json());
            
            if (response.success) {
                setPickupRequest(response.data);
            } else {
                setError(response.message || 'Failed to fetch details');
            }
        } catch (err) {
            setError(sanitizeError(err.message || 'Failed to connect to server'));
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled': return 'bg-blue-100 text-blue-700';
            case 'picked_up': return 'bg-green-100 text-green-700';
            case 'cancelled': return 'bg-red-100 text-red-700';
            default: return 'bg-yellow-100 text-yellow-700';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error || !pickupRequest) {
        return (
            <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
                <p className="text-gray-600 mb-6">{error || 'Pickup request not found'}</p>
                <button 
                    onClick={() => navigate('/pickup-requests')}
                    className="px-6 py-2 bg-[#131842] text-white rounded-lg hover:bg-opacity-90 transition"
                >
                    Back to Pickup Requests
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6 font-sans text-[#1a2b4b]">
            {/* Breadcrumbs */}
            <button 
                onClick={() => navigate('/pickup-requests')}
                className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-blue-600 mb-6 transition"
            >
                <ArrowLeft size={16} />
                Back to Pickup Requests
            </button>

            {/* Header Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <div className="p-6 md:p-8 bg-[#1318420D]">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <span className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1 block">
                                Pickup ID
                            </span>
                            <h1 className="text-2xl font-bold text-[#131842] flex items-center gap-3">
                                {pickupRequest.pickupId || pickupRequest.id}
                                <span className={`text-xs px-3 py-1 rounded-full uppercase font-bold ${getStatusColor(pickupRequest.status)}`}>
                                    {pickupRequest.status}
                                </span>
                            </h1>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-500 mb-1">Requested On</p>
                            <p className="text-lg font-bold">
                                {new Date(pickupRequest.requestedOn).toLocaleDateString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Location Info */}
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <MapPin className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">Pickup Location</h3>
                            <p className="font-bold text-gray-800">{pickupRequest.pickupLocation?.name || pickupRequest.location}</p>
                            <p className="text-sm text-gray-500 leading-relaxed mt-1">
                                {pickupRequest.pickupLocation?.address}<br/>
                                {pickupRequest.pickupLocation?.city}, {pickupRequest.pickupLocation?.pincode}
                            </p>
                        </div>
                    </div>

                    {/* Contact Person */}
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                            <User className="text-orange-600" size={24} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">Contact Person</h3>
                            <p className="font-bold text-gray-800">{pickupRequest.pickupLocation?.contactPerson || 'N/A'}</p>
                            <p className="text-sm text-gray-500 mt-1">{pickupRequest.pickupLocation?.phone || 'N/A'}</p>
                        </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Package className="text-emerald-600" size={24} />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">Shipment Summary</h3>
                            <p className="font-bold text-gray-800">{pickupRequest.expectedAWBs || 0} Total AWBs</p>
                            <p className="text-sm text-gray-500 mt-1">{pickupRequest.pickedAWBs || 0} Successfully Picked</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* AWBs List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center">
                    <h2 className="font-bold text-lg">Included Shipments</h2>
                    <div className="text-sm text-gray-500 font-medium">
                        {pickupRequest.awbDetails?.length || 0} items
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                <th className="px-6 py-4">AWB Number</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Last Update</th>
                                <th className="px-6 py-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {pickupRequest.awbDetails?.length > 0 ? pickupRequest.awbDetails.map((awb, index) => (
                                <tr key={index} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-bold text-blue-600 uppercase">
                                        {awb.number}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700">
                                            {awb.status || 'PENDING'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {awb.lastUpdate ? new Date(awb.lastUpdate).toLocaleDateString('en-IN') : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button 
                                            onClick={() => navigate(`/order-details?id=${awb.orderId}`)}
                                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline uppercase tracking-tight"
                                        >
                                            View Order
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-12 text-center text-gray-400 italic">
                                        No AWBs found in this request
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PickupRequestDetails;
